import {
  SettingsIndustriesResponse,
  SubIndustriesResponse
} from "@/entities/settings/types";
import { RawLookupItem } from "./hooks/useLookupValues";
import { INDUSTRY_FIELD_KEY, SUB_INDUSTRY_FIELD_KEY } from "./const";

type UsageRoute =
  | "settings/industries/usage"
  | "settings/subIndustries/usage";

// i18n key + English fallback (copy is rendered via t(key, fallback)).
interface LocalizedText {
  key: string;
  fallback: string;
}

// Scoped-lookup config (e.g. sub-industries under an industry).
interface LookupParentConfig {
  listRoute: string;
  selectItems: (data: unknown) => RawLookupItem[];
  // Path-param name shared by the values read, usage read, and bulk save.
  pathParam: string;
  bulkUpdateRoute: string;
  label: LocalizedText;
  helpText: LocalizedText;
  placeholder: LocalizedText;
}

export interface LookupFieldConfig {
  listRoute: string;
  selectItems: (data: unknown) => RawLookupItem[];
  usageRoute: UsageRoute;
  buildUsagePathParams: (
    itemId: number | string,
    parentId: number | string | null
  ) => Record<string, string>;
  // Absent ⇒ values save through the generic field-options bulk-update path.
  parent?: LookupParentConfig;
}

const selectIndustries = (data: unknown): RawLookupItem[] =>
  (data as SettingsIndustriesResponse | undefined)?.industries ?? [];

const selectSubIndustries = (data: unknown): RawLookupItem[] =>
  (data as SubIndustriesResponse | undefined) ?? [];

const SUB_INDUSTRY_TRANSLATION_KEY =
  "settings.deals.fieldManagement.subIndustryPanel";

/** Configurable system fields whose values live in a lookup table. Add a field = a registry entry + its settingsapi endpoints, no new component. */
export const LOOKUP_FIELDS: Record<string, LookupFieldConfig> = {
  [INDUSTRY_FIELD_KEY]: {
    listRoute: "settings/industries",
    selectItems: selectIndustries,
    usageRoute: "settings/industries/usage",
    buildUsagePathParams: (itemId) => ({ industryId: String(itemId) })
  },
  [SUB_INDUSTRY_FIELD_KEY]: {
    listRoute: "settings/subIndustriesByIndustry",
    selectItems: selectSubIndustries,
    usageRoute: "settings/subIndustries/usage",
    buildUsagePathParams: (itemId, parentId) => ({
      industryId: String(parentId),
      subIndustryId: String(itemId)
    }),
    parent: {
      listRoute: "settings/industries",
      selectItems: selectIndustries,
      pathParam: "industryId",
      bulkUpdateRoute: "settings/subIndustries/bulkUpdate",
      label: {
        key: `${SUB_INDUSTRY_TRANSLATION_KEY}.industry`,
        fallback: "Industry"
      },
      helpText: {
        key: `${SUB_INDUSTRY_TRANSLATION_KEY}.helpText`,
        fallback: "Select an industry to edit its sub-industry entries"
      },
      placeholder: {
        key: `${SUB_INDUSTRY_TRANSLATION_KEY}.select`,
        fallback: "Select"
      }
    }
  }
};

export const getLookupFieldConfig = (
  fieldKey: string | undefined | null
): LookupFieldConfig | undefined =>
  fieldKey ? LOOKUP_FIELDS[fieldKey] : undefined;
